import { faChevronLeft, faTimes } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import React from 'react';

/** Shared classes for circular icon controls in bottom sheets (back, close, chat, etc.). */
export const drawerSheetHeaderIconButtonClass =
  'flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200 active:bg-gray-200';

export interface IDrawerSheetHeaderProps {
  onClose: () => void;
  closeAriaLabel?: string;
  onBack?: () => void;
  backAriaLabel?: string;
  /** Plain string titles get default typography; pass a ReactNode for custom blocks (e.g. chat header). */
  title: React.ReactNode;
  /** Shown under title when `title` is a string. */
  subtitle?: string;
  /** Extra nodes before the close button (pills, chat icon, …). */
  trailing?: React.ReactNode;
  /**
   * `left`: back (optional) + title block + trailing + close (calendar, inbox list, profile).
   * `center`: back + centered title + trailing + close (inbox chat).
   */
  titleAlign: 'left' | 'center';
  className?: string;
}

const DrawerSheetHeader: React.FC<IDrawerSheetHeaderProps> = ({
  onClose,
  closeAriaLabel = 'Close',
  onBack,
  backAriaLabel = 'Back',
  title,
  subtitle,
  trailing,
  titleAlign,
  className,
}) => {
  const backBtn = onBack ? (
    <button
      type="button"
      onClick={onBack}
      className={drawerSheetHeaderIconButtonClass}
      aria-label={backAriaLabel}
    >
      <FontAwesomeIcon icon={faChevronLeft} className="text-lg" />
    </button>
  ) : (
    <span className="w-8 shrink-0" aria-hidden />
  );

  const closeBtn = (
    <button
      type="button"
      onClick={onClose}
      className={drawerSheetHeaderIconButtonClass}
      aria-label={closeAriaLabel}
    >
      <FontAwesomeIcon icon={faTimes} />
    </button>
  );

  const headerChrome =
    'h-14 shrink-0 gap-2 border-b border-gray-200 bg-white px-4';

  const titleBlock =
    typeof title === 'string' ? (
      <div
        className={classNames(
          'min-w-0',
          titleAlign === 'left' && 'flex-1',
          titleAlign === 'center' && 'w-full text-center'
        )}
      >
        <p className="truncate text-base font-semibold text-gray-900">{title}</p>
        {subtitle ? (
          <p className="mt-0.5 truncate text-xs uppercase tracking-wide text-gray-500">
            {subtitle}
          </p>
        ) : null}
      </div>
    ) : (
      <div
        className={classNames(
          'min-w-0',
          titleAlign === 'left' && 'flex-1',
          titleAlign === 'center' &&
            'flex w-full flex-col items-center justify-center px-1 text-center'
        )}
      >
        {title}
      </div>
    );

  if (titleAlign === 'center') {
    return (
      <header
        className={classNames(
          'grid grid-cols-[2rem_minmax(0,1fr)_auto] items-center',
          headerChrome,
          className
        )}
      >
        <div className="flex items-center justify-start">
          {onBack ? backBtn : <span className="h-8 w-8 shrink-0" aria-hidden />}
        </div>
        <div className="flex min-w-0 items-center justify-center overflow-hidden">
          <div className="min-w-0 max-w-full">{titleBlock}</div>
        </div>
        <div className="flex shrink-0 items-center justify-end gap-2">
          {trailing}
          {closeBtn}
        </div>
      </header>
    );
  }

  return (
    <header
      className={classNames('flex items-center', headerChrome, className)}
    >
      {onBack ? backBtn : null}
      {titleBlock}
      <div className="flex shrink-0 items-center gap-2">{trailing}</div>
      {closeBtn}
    </header>
  );
};

export default DrawerSheetHeader;
